import { AspectRatio, Box, Button, Image } from '@chakra-ui/react';
import { Kitchen3dController as TdController, RENDERING_CAPTURE_ASPECT } from '@rta/rta-blueprint-3d-component';
import { useState, type JSX } from 'react';

import { useSetRendering } from '@hooks';
import { RenderingPreviewSurface, type PreviewCapture } from './rendering-preview-surface';

type RenderingCaptureBoxProps = {
	tdController: TdController | null;
	active: boolean;
};

/**
 * Live 3D box + "Set As Rendering" button, shared by the save modal, the stale-rendering warning,
 * and the rendering panel. The box is an orbitable 3D instance the user frames themselves, and the
 * capture is taken from that same engine — so the staged image is always the view on screen.
 * Capturing writes the rendering immediately via useSetRendering (in-memory + API) — independent
 * of the design save.
 */
export const RenderingCaptureBox = (props: RenderingCaptureBoxProps): JSX.Element => {
	const { tdController, active } = props;
	const { setRendering, isSaving } = useSetRendering();

	const [capture, setCapture] = useState<PreviewCapture | null>(null);
	const [isCapturing, setIsCapturing] = useState(false);
	const [capturedThumb, setCapturedThumb] = useState<string | null>(null);

	const handleSetAsRendering = async (): Promise<void> => {
		if (!capture || !tdController) return;
		setIsCapturing(true);
		try {
			const raw = await capture(2);
			if (raw) {
				// Bound to the box's own controller — this box is also opened for designs other than the one loaded in the editor
				const saved = await setRendering({ image: raw, kitchen: tdController.kitchen });
				if (saved) setCapturedThumb(raw);
			}
		} finally {
			setIsCapturing(false);
		}
	};

	return <Box position="relative" mt={2}>
		<RenderingPreviewSurface
			tdController={tdController}
			active={active}
			onCaptureReady={(fn) => setCapture(() => fn)}
		/>
		<Button
			mt={2}
			size="sm"
			width="100%"
			isLoading={isCapturing || isSaving || !capture}
			loadingText={capture ? 'Capturing...' : 'Rendering...'}
			isDisabled={!capture || isCapturing || isSaving}
			onClick={handleSetAsRendering}
		>
			Set As Rendering
		</Button>
		{capturedThumb && (
			<AspectRatio mt={2} ratio={RENDERING_CAPTURE_ASPECT} w="100%" borderRadius="md" overflow="hidden" bg="gray.50">
				{/* Boxed so AspectRatio's `& > img { object-fit: cover }` cannot crop the capture */}
				<Box><Image src={capturedThumb} alt="Captured rendering" w="100%" h="100%" objectFit="contain" /></Box>
			</AspectRatio>
		)}
	</Box>;
};
